import React, { useState, useEffect } from 'react';
import { Table, Badge, Button, Pagination, Row, Col, Form, Alert, Modal, Card } from 'react-bootstrap';
import { FaLock, FaUnlock, FaDownload, FaEdit, FaCalendarAlt, FaFilePdf, FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaExclamationTriangle, FaSortAmountUp, FaChartBar, FaTrashAlt } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getAttendance, getMonthlyAttendance, deleteAttendanceForMonth } from '../../services/firebase/attendanceService';
import { getEmployees } from '../../services/firebase/employeeService';
import { useAuth } from '../../context/AuthContext';
import EmployeeSelector from './calendar/EmployeeSelector';
import { isDeptVisibleToUser } from '../../utils/departmentVisibility';
import AttendanceFilters from './AttendanceFilters';
import EditAttendanceModal from './EditAttendanceModal';
import { getFestivals } from '../../services/firebase/festivalService';

const AttendanceTable = ({ month, year, isLocked, onLockToggle }) => {
  const { user } = useAuth();
  const isEmployee = user?.role === 'employee';
  const isHROrAdmin = ['admin', 'hr', 'manager', 'ceo', 'founder'].includes((user?.role || '').toLowerCase()) || (user?.designation || '').toLowerCase().includes('founder');

  const [records, setRecords] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(isEmployee ? [user.employeeCode || user.employeeId || user.uid] : []); // Array for multi-select
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'legacy'
  const [activeQuickFilter, setActiveQuickFilter] = useState(null); // 'Present', 'Absent', etc.
  const [showRanking, setShowRanking] = useState(false);
  const [rankingSort, setRankingSort] = useState('late');
  const [activeFilters, setActiveFilters] = useState({});
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mobileViewMode, setMobileViewMode] = useState('cards'); // 'cards' or 'table' on mobile

  const normalizeEmpId = (id) => {
    if (id === null || id === undefined) return '';
    const s = String(id).trim();
    if (/^\d+$/.test(s)) return String(parseInt(s, 10));
    return s.toLowerCase();
  };

  // Helper to determine which monthly collections to fetch based on selected date range
  const getMonthsToFetch = (baseMonth, baseYear, startDate, endDate) => {
    if (!startDate && !endDate) {
      return [{ month: baseMonth, year: baseYear }];
    }

    let startY = baseYear;
    let startM = baseMonth;
    let endY = baseYear;
    let endM = baseMonth;

    if (startDate) {
      const s = moment(startDate, 'YYYY-MM-DD');
      if (s.isValid()) {
        startY = s.year();
        startM = s.month() + 1;
      }
    }

    if (endDate) {
      const e = moment(endDate, 'YYYY-MM-DD');
      if (e.isValid()) {
        endY = e.year();
        endM = e.month() + 1;
      }
    }

    if (startDate && !endDate) {
      endY = startY;
      endM = startM;
    } else if (!startDate && endDate) {
      startY = endY;
      startM = endM;
    }

    // Ensure start <= end
    if (startY * 12 + startM > endY * 12 + endM) {
      const tempY = startY; startY = endY; endY = tempY;
      const tempM = startM; startM = endM; endM = tempM;
    }

    const list = [];
    let curY = startY;
    let curM = startM;
    let count = 0;
    while ((curY < endY || (curY === endY && curM <= endM)) && count < 24) {
      list.push({ month: curM, year: curY });
      curM++;
      if (curM > 12) {
        curM = 1;
        curY++;
      }
      count++;
    }

    return list.length > 0 ? list : [{ month: baseMonth, year: baseYear }];
  };

  const getNormalizedDate = (r) => {
    if (!r) return '';
    if (r.dateString && /^\d{4}-\d{2}-\d{2}$/.test(String(r.dateString).trim())) {
      return String(r.dateString).trim();
    }
    let val = r.date || r.dateString;
    if (!val) return '';

    if (val.seconds || val._seconds) {
      val = new Date((val.seconds || val._seconds) * 1000);
    }

    if (val instanceof Date && !isNaN(val.getTime())) {
      const y = val.getFullYear();
      const m = String(val.getMonth() + 1).padStart(2, '0');
      const d = String(val.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    if (typeof val === 'string') {
      const s = val.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      const m = moment(s, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY/MM/DD', moment.ISO_8601]);
      if (m.isValid()) return m.format('YYYY-MM-DD');
    }

    return '';
  };

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      // Fetch festivals
      try {
        const festRes = await getFestivals();
        setFestivals(Array.isArray(festRes) ? festRes : []);
      } catch (e) {
        console.warn('Could not load festivals:', e);
      }

      // Fetch employees for department mapping and inactive filtering
      const empRes = await getEmployees({ includeInactive: true });
      const allEmps = empRes?.data || [];
      const empIdToDeptMap = new Map();
      const inactiveEmpIds = new Set();
      const normalizeId = (id) => /^\d+$/.test(id) ? String(parseInt(id, 10)) : String(id).toLowerCase().trim();
      allEmps.forEach(emp => {
        const id1 = normalizeId(emp.employeeId);
        const id2 = normalizeId(emp.id || emp.uid);
        if (id1) empIdToDeptMap.set(id1, emp.department);
        if (id2) empIdToDeptMap.set(id2, emp.department);
        if (emp.isActive === false || emp.status === 'Inactive') {
          if (id1) inactiveEmpIds.add(id1);
          if (id2) inactiveEmpIds.add(id2);
        }
      });

      const monthsToFetch = getMonthsToFetch(month, year, activeFilters.startDate, activeFilters.endDate);

      let empCodeToUse = null;
      if (isEmployee) {
        empCodeToUse = selectedEmployeeIds[0] || user.uid;
      } else if (selectedEmployeeIds.length === 1) {
        empCodeToUse = selectedEmployeeIds[0];
      }

      let rawRecords = [];

      if (viewMode === 'monthly') {
        const responses = await Promise.all(
          monthsToFetch.map(mInfo => getMonthlyAttendance(mInfo.month, mInfo.year, empCodeToUse))
        );
        responses.forEach(res => {
          const recs = res?.data || res || [];
          if (Array.isArray(recs)) rawRecords.push(...recs);
        });
      } else {
        const responses = await Promise.all(
          monthsToFetch.map(mInfo => {
            const params = {
              month: mInfo.month,
              year: mInfo.year,
              page: pagination.page,
              limit: pagination.limit
            };
            if (selectedEmployeeIds.length === 1) {
              params.employeeId = selectedEmployeeIds[0];
            }
            return getAttendance(params);
          })
        );
        responses.forEach(res => {
          const recs = res?.data || res || [];
          if (Array.isArray(recs)) rawRecords.push(...recs);
        });
      }

      // Exclude records of inactive employees
      let records = rawRecords.filter(r => {
        const normId = normalizeId(r.empCode || r.employeeId || r.uid || '');
        if (inactiveEmpIds.has(normId)) return false;
        return true;
      });

      // Deduplicate records by doc id or unique employee + date
      const seen = new Set();
      records = records.filter(r => {
        const normDate = getNormalizedDate(r);
        const normId = normalizeId(r.empCode || r.employeeId || r.uid || '');
        const key = r.id || `${normId}_${normDate}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const isDeptOrProdHead = user?.role === 'dept_head' || user?.role === 'production_head';
      if (isDeptOrProdHead) {
        records = records.filter(r => {
          const normId = normalizeId(r.empCode || r.employeeId || r.uid || '');
          const actualDept = empIdToDeptMap.get(normId) || r.department || r.dept || '';
          return isDeptVisibleToUser(actualDept, actualDept, user);
        });
      }
      setRecords(Array.isArray(records) ? records : []);
      setPagination({ page: 1, limit: 100, total: records.length || 0, totalPages: 1 });
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset filters when month or year dropdown changes
  useEffect(() => {
    setActiveFilters({});
    setActiveQuickFilter(null);
  }, [month, year]);

  useEffect(() => {
    fetchAttendance();
  }, [month, year, pagination.page, selectedEmployeeIds, viewMode, activeFilters.startDate, activeFilters.endDate]);

  useEffect(() => {
    applyFilters();
  }, [records, activeFilters, activeQuickFilter]);

  const applyFilters = () => {
    try {
      let result = [...records];

      // 1. Multi-Employee Filter
      if (!isEmployee && selectedEmployeeIds.length > 0) {
        const normSelected = selectedEmployeeIds.map(id => normalizeEmpId(id));
        result = result.filter(r => {
          const id1 = normalizeEmpId(r.empCode);
          const id2 = normalizeEmpId(r.employeeId);
          const id3 = normalizeEmpId(r.uid);
          return normSelected.includes(id1) || normSelected.includes(id2) || normSelected.includes(id3);
        });
      }

      // 2. Dashboard Card Filters (Priority on stat card click)
      if (activeQuickFilter) {
        if (activeQuickFilter === 'Present') {
          result = result.filter(r => isRecordPresent(r));
        } else {
          result = result.filter(r => getEffectiveStatus(r) === activeQuickFilter);
        }
      }

      // 3. Status Dropdown Filter
      if (activeFilters.status) {
        const target = activeFilters.status.toLowerCase().replace(/[\s-_]/g, '');
        result = result.filter(r => {
          const s = getEffectiveStatus(r);
          const sNorm = s.toLowerCase().replace(/[\s-_]/g, '');
          const rawStatusNorm = (r.status || '').toLowerCase().replace(/[\s-_]/g, '');

          if (target === 'present') {
            return sNorm === 'present' || rawStatusNorm === 'present' || isRecordPresent(r);
          }
          if (target === 'absent') {
            return sNorm === 'absent' || rawStatusNorm === 'absent';
          }
          if (target === 'late') {
            return sNorm === 'late' || Boolean(r.lateBy && r.lateBy !== '-' && r.lateBy !== '00:00' && r.lateBy !== '0:00');
          }
          if (target === 'earlyexit' || target === 'early' || target === 'earlygoing') {
            return sNorm === 'earlyexit' || Boolean(r.earlyGoingBy && r.earlyGoingBy !== '-' && r.earlyGoingBy !== '00:00' && r.earlyGoingBy !== '0:00');
          }
          if (target === 'misspunch') {
            return sNorm === 'misspunch' || rawStatusNorm.includes('miss');
          }
          if (target === 'halfday' || target === 'halfdays') {
            return sNorm === 'halfdays' || rawStatusNorm.includes('half');
          }
          if (target === 'weeklyoff') {
            return sNorm === 'weeklyoff' || rawStatusNorm.includes('weekly');
          }
          if (target === 'leave') {
            return sNorm === 'leave' || rawStatusNorm.includes('leave');
          }
          if (target === 'festival') {
            return sNorm === 'festival' || rawStatusNorm.includes('festival');
          }
          if (target === 'extrawork') {
            return sNorm === 'extrawork' || rawStatusNorm.includes('extra');
          }
          return sNorm === target || rawStatusNorm === target;
        });
      }

      // 4. Optional Search Text
      if (activeFilters.searchText) {
        const search = activeFilters.searchText.toLowerCase().trim();
        result = result.filter(r =>
          (r.empName || r.employeeName || '').toLowerCase().includes(search) ||
          (r.empCode || r.employeeId || '').toLowerCase().includes(search)
        );
      }

      // 5. Date Range Filters (From and To)
      if (activeFilters.startDate) {
        result = result.filter(r => {
          const d = getNormalizedDate(r);
          return d ? d >= activeFilters.startDate : false;
        });
      }
      if (activeFilters.endDate) {
        result = result.filter(r => {
          const d = getNormalizedDate(r);
          return d ? d <= activeFilters.endDate : false;
        });
      }

      // 6. Sidebar Checkbox Filters (Quick Insights)
      const hasQuickInsightFilter = activeFilters.isLate || activeFilters.isEarly ||
        activeFilters.isMissPunch || activeFilters.isHalfDay ||
        activeFilters.isAbsent;

      if (hasQuickInsightFilter) {
        const hasPunch = (v) => v && v !== '-' && v !== '' && v !== '00:00' && v !== '0:00' && /[1-9]/.test(String(v));
        result = result.filter(r => {
          const s = getEffectiveStatus(r);
          const isLate = Boolean((r.lateBy && r.lateBy !== '-' && r.lateBy !== '00:00' && r.lateBy !== '0:00') || s === 'Late');
          const isEarly = Boolean((r.earlyGoingBy && r.earlyGoingBy !== '-' && r.earlyGoingBy !== '00:00' && r.earlyGoingBy !== '0:00') || s === 'Early Exit');
          const isMiss = Boolean(s === 'Miss Punch' || ((hasPunch(r.inTime) && !hasPunch(r.outTime)) || (!hasPunch(r.inTime) && hasPunch(r.outTime))));
          const isHalf = Boolean(s === 'Half Days' || (r.status || '').toLowerCase().includes('half'));
          const isAbs = Boolean(s === 'Absent' || (r.status || '').toLowerCase().includes('absent'));

          if (activeFilters.isLate && isLate) return true;
          if (activeFilters.isEarly && isEarly) return true;
          if (activeFilters.isMissPunch && isMiss) return true;
          if (activeFilters.isHalfDay && isHalf) return true;
          if (activeFilters.isAbsent && isAbs) return true;
          return false;
        });
      }

      // Sort by Date (ascending), then Employee ID (ascending)
      result.sort((a, b) => {
        const dateA = getNormalizedDate(a);
        const dateB = getNormalizedDate(b);
        if (dateA !== dateB) {
          return dateA.localeCompare(dateB);
        }
        const idA = String(a.empCode || a.employeeId || '').toLowerCase();
        const idB = String(b.empCode || b.employeeId || '').toLowerCase();
        return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: 'base' });
      });

      setFilteredRecords(result);
    } catch (error) {
      console.error('Error applying filters:', error);
      setFilteredRecords(records);
    }
  };

  const renderRankingTable = (rData) => {
    if (!isHROrAdmin || rData.length === 0) return null;

    // Generate summary map
    const summaryMap = {};
    rData.forEach(r => {
      const id = r.empCode || r.employeeId || r.uid;
      const name = r.empName || r.employeeName || 'Unknown';
      if (!summaryMap[id]) {
        summaryMap[id] = { id, name, present: 0, absent: 0, late: 0, missPunch: 0, earlyExit: 0, half: 0, otMins: 0 };
      }
      const s = getEffectiveStatus(r);
      if (isRecordPresent(r)) summaryMap[id].present++;
      if (s === 'Absent') summaryMap[id].absent++;
      if (s === 'Late') summaryMap[id].late++;
      if (s === 'Miss Punch') summaryMap[id].missPunch++;
      if (s === 'Early Exit') summaryMap[id].earlyExit++;
      if (s === 'Half Days') summaryMap[id].half++;

      // Calculate OT minutes for ranking
      if (r.outTime && r.outTime !== '-') {
        const out = moment(r.outTime, ["HH:mm", "h:mm A", "H:mm"]);
        if (out.isValid()) {
          const outMins = out.hours() * 60 + out.minutes();
          const threshold = 18 * 60 + 30; // 6:30 PM
          if (outMins > threshold) {
            summaryMap[id].otMins += (outMins - threshold);
          }
        }
      }
    });

    const rankingData = Object.values(summaryMap).map(emp => ({
      ...emp,
      otFormatted: `${Math.floor(emp.otMins / 60)}h ${emp.otMins % 60}m`
    }));

    // Grouping by sort key
    rankingData.sort((a, b) => b[rankingSort] - a[rankingSort]);

    return (
      <div className="bg-white p-3 p-md-4 rounded-4 shadow-sm mb-4 border-primary border-top border-4">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
          <h6 className="fw-bold mb-0 text-primary">
            <FaChartBar className="me-2" /> Employee Wise Attendance Ranking
          </h6>
          <div className="d-flex gap-2">
            <Button
              variant="outline-danger"
              size="sm"
              className="rounded-pill px-3 shadow-sm d-flex align-items-center fw-bold"
              onClick={() => handleDownloadRankingPDF(rankingData)}
            >
              <FaFilePdf className="me-2" /> Download Report
            </Button>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2 align-items-center overflow-auto py-1 w-100 flex-nowrap" style={{ scrollbarWidth: 'none' }}>
            <small className="text-muted fw-bold me-2 d-none d-md-inline">Rank By:</small>
            {[
              { id: 'late', label: 'Late', color: 'warning' },
              { id: 'missPunch', label: 'Miss Punch', color: 'secondary' },
              { id: 'absent', label: 'Absent', color: 'danger' },
              { id: 'earlyExit', label: 'Early Exit', color: 'warning' },
              { id: 'otMins', label: 'Overtime', color: 'success' }
            ].map(opt => (
              <Button
                key={opt.id}
                variant={rankingSort === opt.id ? opt.color : `outline-${opt.color}`}
                size="sm"
                className="rounded-pill px-3 fw-bold text-nowrap"
                style={{ fontSize: '0.75rem' }}
                onClick={() => setRankingSort(opt.id)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="table-responsive attendance-table-container">
          <Table hover size="sm" className="mb-0 align-middle">
            <thead>
              <tr className="bg-light">
                <th className="py-3 border-0 small text-muted ps-3">STAFF NAME</th>
                <th className="py-3 border-0 small text-center text-muted">PRESENT</th>
                <th className="py-3 border-0 small text-center text-muted">ABSENT</th>
                <th className="py-3 border-0 small text-center text-muted">LATE</th>
                <th className="py-3 border-0 small text-center text-muted">MISS PUNCH</th>
                <th className="py-3 border-0 small text-center text-muted">EARLY</th>
                <th className="py-3 border-0 small text-center text-muted">OVERTIME</th>
              </tr>
            </thead>
            <tbody>
              {rankingData.map(emp => (
                <tr key={emp.id} className={emp[rankingSort] > 0 ? '' : 'text-muted opacity-75'}>
                  <td className="py-3 ps-3">
                    <div className="fw-bold text-dark">{emp.name}</div>
                    <small className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{emp.id}</small>
                  </td>
                  <td className="py-2 text-center text-success fw-bold">{emp.present}</td>
                  <td className={`py-2 text-center fw-bold ${rankingSort === 'absent' ? 'bg-danger-subtle text-danger' : 'text-danger'}`}>{emp.absent}</td>
                  <td className={`py-2 text-center fw-bold ${rankingSort === 'late' ? 'bg-warning-subtle text-warning-emphasis' : 'text-warning-emphasis'}`}>{emp.late}</td>
                  <td className={`py-2 text-center fw-bold ${rankingSort === 'missPunch' ? 'bg-secondary-subtle text-secondary' : 'text-secondary'}`}>{emp.missPunch}</td>
                  <td className={`py-2 text-center fw-bold ${rankingSort === 'earlyExit' ? 'bg-warning-subtle text-orange' : ''}`}>{emp.earlyExit}</td>
                  <td className={`py-2 text-center fw-bold ${rankingSort === 'otMins' ? 'bg-success-subtle text-success' : 'text-success'}`}>{emp.otFormatted}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const handleDownloadRankingPDF = (rankingData) => {
    try {
      setLoading(true);
      const doc = new jsPDF();
      const monthName = getMonthName(month);

      // Branded Header
      doc.setFontSize(22);
      doc.setTextColor(25, 118, 210); // Professional Blue
      doc.text("Employee Discipline Ranking Report", 105, 18, { align: 'center' });

      doc.setFontSize(11);
      doc.setTextColor(80, 80, 80);
      doc.text(`Monthly Summary Audit - ${monthName} ${year}`, 105, 25, { align: 'center' });
      doc.line(15, 30, 195, 30); // Separator line

      const head = [['Staff Name', 'Employee ID', 'Present', 'Absent', 'Late', 'Miss Punch', 'Early Exit', 'Overtime']];
      const body = rankingData.map(emp => [
        emp.name,
        emp.id,
        emp.present,
        emp.absent,
        emp.late,
        emp.missPunch,
        emp.earlyExit,
        emp.otFormatted
      ]);

      autoTable(doc, {
        head: head,
        body: body,
        startY: 35,
        theme: 'striped',
        headStyles: {
          fillColor: [25, 118, 210],
          textColor: 255,
          fontSize: 10,
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: 50, fontStyle: 'bold' }, // Name
          1: { cellWidth: 30, halign: 'center' }, // ID
          2: { halign: 'center' },
          3: { halign: 'center', textColor: [211, 47, 47] }, // Absent Red
          4: { halign: 'center', textColor: [237, 108, 2] }, // Late Orange
          5: { halign: 'center' },
          6: { halign: 'center' },
          7: { halign: 'center', textColor: [40, 167, 69] } // Success Green
        },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        styles: { fontSize: 9, cellPadding: 3 },
        margin: { top: 35 }
      });

      // Simple Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, 105, doc.internal.pageSize.height - 10, { align: 'center' });
        doc.text(`Official Document - Generated on ${new Date().toLocaleDateString()}`, 15, doc.internal.pageSize.height - 10);
      }

      doc.save(`Attendance_Ranking_${monthName}_${year}.pdf`);
    } catch (error) {
      console.error("Ranking PDF failed:", error);
      alert("Failed to generate Ranking PDF report.");
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveStatus = (r) => {
    if (!r) return 'Absent';

    // Parse date for Tuesday check (Day 2 in JS)
    let dateObj;
    if (r.date && (r.date._seconds || r.date.seconds)) {
      dateObj = new Date((r.date._seconds || r.date.seconds) * 1000);
    } else if (r.date instanceof Date) {
      dateObj = r.date;
    } else if (typeof r.date === 'string') {
      const parsedM = moment(r.date, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', moment.ISO_8601]);
      dateObj = parsedM.isValid() ? parsedM.toDate() : new Date(r.date);
    } else {
      dateObj = new Date(r.date);
    }

    const dayOfWeek = (dateObj && !isNaN(dateObj.getTime())) ? dateObj.getDay() : -1;
    const dateKey = (dateObj && !isNaN(dateObj.getTime())) ? moment(dateObj).format('YYYY-MM-DD') : null;
    const isFestivalDate = Boolean(dateKey && festivals.some(f => moment(f.date).format('YYYY-MM-DD') === dateKey));

    // Punch Detection
    const hasPunch = (v) => v && v !== '-' && v !== '' && v !== '00:00' && v !== '0:00' && /[1-9]/.test(String(v));
    const hasIn = hasPunch(r.inTime);
    const hasOut = hasPunch(r.outTime);

    const s = (r.status || '').toLowerCase();

    // 1. Explicit Overrides (Leave, Half-day, Holiday, Festival, Extra Work, Weekly Off, Miss Punch)
    if (s.includes('leave')) return 'Leave';
    if (s.includes('half')) return 'Half Days';
    if (s.includes('holiday')) return 'Holiday';
    if (s.includes('festival')) return 'Festival';
    if (s.includes('extra work') || s.includes('extrawork')) return 'Extra Work';
    if (s.includes('weekly')) return 'Weekly Off';
    if (s.includes('miss')) return 'Miss Punch';

    // 2. Tuesday Rule (Weekly Off)
    if (dayOfWeek === 2 && !hasIn && !hasOut) return 'Weekly Off';

    // 3. Festival Rule (Except Tuesday): If festival day and no punches or status is Absent -> Festival (paid leave)
    if (isFestivalDate && dayOfWeek !== 2 && ((!hasIn && !hasOut) || s.includes('absent'))) {
      return 'Festival';
    }

    // Explicit Absent override if no punches OR explicitly marked Absent
    if (s.includes('absent') && (!hasIn || !hasOut)) {
      return dayOfWeek === 2 ? 'Weekly Off' : 'Absent';
    }

    // 4. Miss Punch Rule
    if ((hasIn && !hasOut) || (!hasIn && hasOut)) return 'Miss Punch';

    // 5. Absent Rule
    if (!hasIn && !hasOut) return dayOfWeek === 2 ? 'Weekly Off' : 'Absent';

    // 6. Present / Late / Early Exit detection
    const late = String(r.lateBy || '').trim();
    if (late && late !== '00:00' && late !== '0:00' && late !== '-') return 'Late';

    const early = String(r.earlyGoingBy || '').trim();
    if (early && early !== '00:00' && early !== '0:00' && early !== '-') return 'Early Exit';

    return 'Present';
  };

  const isRecordPresent = (r) => {
    const s = getEffectiveStatus(r);
    // Employee is present even if they have Late, Early Exit, Miss Punch, Half Day, or Festival.
    // Festival on non-Tuesday is paid leave and counts as Present!
    // Miss Punch and Early Exit counts should NOT reduce the Present count.
    // Only Absent and Weekly Off (and Leave/Holiday) reduce the Present count.
    return s === 'Present' || s === 'Late' || s === 'Early Exit' || s === 'Miss Punch' || s === 'Half Days' || s === 'Extra Work' || s === 'Festival';
  };

  const getStatusBadge = (record) => {
    const status = getEffectiveStatus(record);

    switch (status) {
      case 'Present': return <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm"><FaCheckCircle className="me-1" /> Present</Badge>;
      case 'Absent': return <Badge bg="danger" className="px-3 py-2 rounded-pill shadow-sm"><FaTimesCircle className="me-1" /> Absent</Badge>;
      case 'Late': return <Badge bg="warning" text="dark" className="px-3 py-2 rounded-pill shadow-sm"><FaClock className="me-1" /> Late</Badge>;
      case 'Half Days': return <Badge bg="info" className="px-3 py-2 rounded-pill shadow-sm">Half Day</Badge>;
      case 'Miss Punch': return <Badge bg="secondary" className="px-3 py-2 rounded-pill shadow-sm"><FaExclamationTriangle className="me-1" /> Miss Punch</Badge>;
      case 'Leave': return <Badge bg="primary" className="px-3 py-2 rounded-pill shadow-sm">Leave</Badge>;
      case 'Weekly Off': return <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border shadow-sm">Weekly Off</Badge>;
      case 'Early Exit': return <Badge bg="warning" className="px-3 py-2 rounded-pill shadow-sm">Early Exit</Badge>;
      case 'Extra Work': return <Badge bg="info" className="px-3 py-2 rounded-pill shadow-sm">Extra Work</Badge>;
      case 'Festival': return <Badge className="px-3 py-2 rounded-pill shadow-sm festival-badge-rainbow">🎉 Festival</Badge>;
      default: return <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border">{String(status)}</Badge>;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    if (date.seconds || date._seconds) {
      return moment((date.seconds || date._seconds) * 1000).format('DD/MM/YYYY');
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${d}/${m}/${y}`;
    }
    if (typeof date === 'string') {
      const s = date.trim();
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
      const m = moment(s, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', moment.ISO_8601]);
      if (m.isValid()) return m.format('DD/MM/YYYY');
    }
    return String(date);
  };

  const getMonthName = (m) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[m - 1] || '';
  };

  const exportToExcel = () => {
    try {
      if (filteredRecords.length === 0) {
        alert('No records to export');
        return;
      }
      const ws = XLSX.utils.json_to_sheet(filteredRecords.map(r => ({
        'Employee ID': r.empCode || r.employeeId,
        'Employee Name': r.empName || r.employeeName,
        'Date': formatDate(r.date),
        'In Time': r.inTime || '-',
        'Out Time': r.outTime || '-',
        'Status': getEffectiveStatus(r),
        'Working Hours': r.workDuration || r.workingHours || '-',
        'Overtime': r.overtime || r.overtimeHours || '-'
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
      const filenameSuffix = (activeFilters.startDate || activeFilters.endDate)
        ? `${activeFilters.startDate || 'start'}_to_${activeFilters.endDate || 'end'}`
        : `${getMonthName(month)}_${year}`;
      XLSX.writeFile(wb, `Attendance_Report_${filenameSuffix}.xlsx`);
    } catch (error) {
      console.error('Excel export failed:', error);
      alert('Failed to export to Excel.');
    }
  };

  const handleEmployeeChange = (ids) => {
    setSelectedEmployeeIds(Array.isArray(ids) ? ids : [ids]);
    setActiveQuickFilter(null); // Reset quick filter when selection changes
    setPagination({ ...pagination, page: 1 });
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    fetchAttendance(); // Refresh the table
  };

  const handleDownloadPDF = async () => {
    if (filteredRecords.length === 0) {
      alert('No records to export');
      return;
    }

    setLoading(true);
    try {
      // Standard jspdf initialization
      const doc = new jsPDF({
        orientation: 'l',
        unit: 'mm',
        format: 'a4'
      });
      const monthName = getMonthName(month);
      const periodLabel = (activeFilters.startDate || activeFilters.endDate)
        ? `${activeFilters.startDate ? formatDate(activeFilters.startDate) : 'Start'} to ${activeFilters.endDate ? formatDate(activeFilters.endDate) : 'End'}`
        : `${monthName} ${year}`;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Group records by Employee ID
      const groupedRecords = filteredRecords.reduce((acc, record) => {
        const id = record.empCode || record.employeeId || 'Unknown';
        if (!acc[id]) acc[id] = [];
        acc[id].push(record);
        return acc;
      }, {});

      const employeeIds = Object.keys(groupedRecords).sort();

      employeeIds.forEach((empId, empIdx) => {
        const empRecords = groupedRecords[empId];
        const empName = empRecords[0]?.empName || empRecords[0]?.employeeName || 'Unknown';

        if (empIdx > 0) doc.addPage();

        // Page Header
        doc.setFillColor(0, 51, 102);
        doc.rect(0, 0, pageWidth, 40, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('DIGI SAHYADRI HRM', 20, 15);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'normal');
        doc.text('Staff Attendance Report', 20, 24);

        doc.setFontSize(10);
        doc.text(`Period: ${periodLabel}`, pageWidth - 20, 15, { align: 'right' });
        doc.text(`Generated: ${moment().format('DD-MMM-YYYY HH:mm')}`, pageWidth - 20, 24, { align: 'right' });

        // Individual Employee Context
        doc.setDrawColor(255, 255, 255);
        doc.line(20, 28, 120, 28);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`${empName} [${empId}]`, 20, 35);

        // Individual Statistics Summary for THIS employee on their page
        const p = empRecords.filter(r => isRecordPresent(r)).length;
        const a = empRecords.filter(r => getEffectiveStatus(r) === 'Absent').length;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Present: ${p} | Absent: ${a} | Records: ${empRecords.length}`, pageWidth - 20, 35, { align: 'right' });

        // Table Configuration - Optimized for Landscape (297mm width)
        const col = {
          date: 12,
          shift: 35,
          in: 55,
          out: 75,
          status: 95,
          work: 135, // Increased gap for status
          late: 165,
          early: 195,
          ot: 220,
          note: 245
        };
        const tableTop = 52;

        // Table Header
        doc.setFillColor(230, 230, 230);
        doc.rect(10, tableTop - 6, pageWidth - 20, 10, 'F');
        doc.setTextColor(40, 40, 40);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('DATE', col.date, tableTop);
        doc.text('SHIFT', col.shift, tableTop);
        doc.text('IN', col.in, tableTop);
        doc.text('OUT', col.out, tableTop);
        doc.text('STATUS', col.status, tableTop);
        doc.text('DURATION', col.work, tableTop);
        doc.text('LATE', col.late, tableTop);
        doc.text('EARLY', col.early, tableTop);
        doc.text('OT', col.ot, tableTop);
        doc.text('REMARKS', col.note, tableTop);

        let yPos = tableTop + 10;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);

        empRecords.sort((a, b) => new Date(a.date) - new Date(b.date)).forEach((record, idx) => {
          // Page Break within same employee
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = 20;
            doc.setFillColor(230, 230, 230);
            doc.rect(10, yPos - 6, pageWidth - 20, 10, 'F');
            doc.setFont('helvetica', 'bold');
            doc.text('DATE', col.date, yPos);
            doc.text('SHIFT', col.shift, yPos);
            doc.text('IN', col.in, yPos);
            doc.text('OUT', col.out, yPos);
            doc.text('STATUS', col.status, yPos);
            doc.text('DURATION', col.work, yPos);
            doc.text('LATE', col.late, yPos);
            doc.text('EARLY', col.early, yPos);
            doc.text('OT', col.ot, yPos);
            doc.text('REMARKS', col.note, yPos);
            yPos += 10;
            doc.setFont('helvetica', 'normal');
          }

          if (idx % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(10, yPos - 4.5, pageWidth - 20, 7.5, 'F');
          }

          doc.setTextColor(60, 60, 60);
          doc.text(formatDate(record.date), col.date, yPos);
          doc.text(record.shift || '-', col.shift, yPos);
          doc.text(record.inTime || '-', col.in, yPos);
          doc.text(record.outTime || '-', col.out, yPos);

          const status = (record.status || '-').substring(0, 20); // Truncate long status names
          if (status.includes('Absent')) doc.setTextColor(200, 0, 0);
          else if (status.includes('Present')) doc.setTextColor(0, 128, 0);
          else doc.setTextColor(60, 60, 60);
          doc.text(status, col.status, yPos);
          doc.setTextColor(60, 60, 60);

          doc.text(record.workDuration || record.workingHours || '-', col.work, yPos);

          const late = record.lateBy || '-';
          if (late !== '-' && late !== '00:00' && late !== '0:00') doc.setTextColor(200, 0, 0);
          doc.text(late, col.late, yPos);
          doc.setTextColor(60, 60, 60);

          doc.text(record.earlyGoingBy || '-', col.early, yPos);
          doc.text(record.overtime || '-', col.ot, yPos);
          doc.text('', col.note, yPos); // Placeholder for manually written remarks

          doc.setDrawColor(240, 240, 240);
          doc.line(10, yPos + 3, pageWidth - 10, yPos + 3);
          yPos += 7.5;
        });
      });

      // Footer
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, 290, { align: 'center' });
        doc.text('Digi Sahyadri HRM Software - Official Document', 15, 290);
      }

      const pdfFileSuffix = (activeFilters.startDate || activeFilters.endDate)
        ? `${activeFilters.startDate || 'start'}_to_${activeFilters.endDate || 'end'}`
        : `${monthName}_${year}`;
      doc.save(`Attendance_Report_${pdfFileSuffix}.pdf`);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMonthSheet = async () => {
    setDeleting(true);
    try {
      const res = await deleteAttendanceForMonth(month, year);
      if (res.deletedCount > 0) {
        toast.success(`Successfully deleted ${res.deletedCount} attendance records for ${getMonthName(month)} ${year}`);
      } else {
        toast.info(`No attendance records found for ${getMonthName(month)} ${year}`);
      }
      setShowDeleteModal(false);
      fetchAttendance();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.message || 'Failed to delete attendance sheet');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="bg-white p-3 p-md-4 rounded-4 shadow-sm mb-4 border-top border-4 border-primary border-opacity-25" style={{ overflow: 'visible', zIndex: 100, position: 'relative' }}>
        <Row className="g-3 align-items-center">
          <Col xs={12} md={6} lg={6} className="d-flex align-items-center gap-3" style={{ overflow: 'visible' }}>
            <div className="flex-grow-1" style={{ overflow: 'visible' }}>
              <EmployeeSelector
                selectedEmployeeIds={selectedEmployeeIds}
                onEmployeeChange={handleEmployeeChange}
                disabled={isEmployee}
                month={month}
                year={year}
              />
              {isHROrAdmin && selectedEmployeeIds.length > 0 && (
                <Button
                  variant="link"
                  className="text-decoration-none text-muted p-0 mt-1 d-block"
                  onClick={() => setSelectedEmployeeIds([])}
                >
                  <small className="fw-bold">× Clear Selection</small>
                </Button>
              )}
            </div>
          </Col>
          <Col xs={12} md={6} lg={6} className="d-flex justify-content-start justify-content-md-end align-items-center">
            <div className="attendance-actions-bar">
              <Button
                variant={showRanking ? "primary" : "outline-primary"}
                className="rounded-pill px-3 shadow-sm text-nowrap d-flex align-items-center fw-bold transition-all"
                onClick={() => setShowRanking(!showRanking)}
                style={{ height: '42px' }}
              >
                <FaChartBar className="me-2" /> {showRanking ? "Hide Metrics" : "Ranking Insights"}
              </Button>
              <Button
                variant="outline-success"
                className="rounded-pill px-3 shadow-sm text-nowrap d-flex align-items-center fw-bold"
                onClick={exportToExcel}
                style={{ height: '42px' }}
              >
                <FaDownload className="me-2" /> Excel
              </Button>
              <Button
                variant="outline-danger"
                className="rounded-pill px-3 shadow-sm text-nowrap d-flex align-items-center fw-bold"
                onClick={handleDownloadPDF}
                disabled={loading}
                style={{ height: '42px' }}
              >
                <FaFilePdf className="me-2" /> PDF
              </Button>
              {isHROrAdmin && records.length > 0 && (
                <Button
                  variant="outline-danger"
                  className="rounded-pill px-3 shadow-sm text-nowrap d-flex align-items-center fw-bold"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={loading || deleting}
                  style={{ height: '42px' }}
                  title={`Delete attendance sheet for ${getMonthName(month)} ${year}`}
                >
                  <FaTrashAlt className="me-2" /> Delete Sheet
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* Filters */}
      <AttendanceFilters
        initialFilters={activeFilters}
        onFilterChange={setActiveFilters}
      />

      {/* Active Filter Indicators Bar */}
      {(activeFilters.startDate || activeFilters.endDate || activeFilters.status || activeFilters.isLate || activeFilters.isEarly || activeFilters.isMissPunch || activeFilters.isHalfDay || activeFilters.isAbsent) && (
        <Alert variant="primary" className="mb-3 p-2 px-3 d-flex flex-wrap justify-content-between align-items-center rounded-4 bg-primary-subtle border-0 shadow-sm">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="fw-bold text-primary small">Active Filter:</span>
            {activeFilters.startDate && (
              <Badge bg="white" text="dark" className="border shadow-sm py-1 px-2 fw-semibold">
                From: {formatDate(activeFilters.startDate)}
              </Badge>
            )}
            {activeFilters.endDate && (
              <Badge bg="white" text="dark" className="border shadow-sm py-1 px-2 fw-semibold">
                To: {formatDate(activeFilters.endDate)}
              </Badge>
            )}
            {activeFilters.status && (
              <Badge bg="white" text="dark" className="border shadow-sm py-1 px-2 fw-semibold">
                Status: {activeFilters.status}
              </Badge>
            )}
            {activeFilters.isLate && <Badge bg="warning" text="dark" className="shadow-sm">Late</Badge>}
            {activeFilters.isEarly && <Badge bg="warning" text="dark" className="shadow-sm">Early Going</Badge>}
            {activeFilters.isMissPunch && <Badge bg="secondary" className="shadow-sm">Miss Punch</Badge>}
            {activeFilters.isHalfDay && <Badge bg="info" className="shadow-sm">Half Day</Badge>}
            {activeFilters.isAbsent && <Badge bg="danger" className="shadow-sm">Absent</Badge>}
          </div>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none fw-bold text-primary"
            onClick={() => setActiveFilters({})}
          >
            × Clear All Filters
          </Button>
        </Alert>
      )}

      {/* Attendance Statistics Dashboard */}
      <div className="mb-4">
        {selectedEmployeeIds.length > 0 && filteredRecords.length > 0 && (
          <div className="d-flex align-items-center mb-2 px-1 text-primary">
            <FaUsers className="me-2" />
            <h6 className="mb-0 fw-bold">
              Analysis for {selectedEmployeeIds.length} Selected Member(s)
            </h6>
          </div>
        )}
        <Row className="g-3 px-1 mt-1">
          {(() => {
            // Calculate stats on the RECORDS for the selected employee(s) and date range
            const normSelected = selectedEmployeeIds.map(id => normalizeEmpId(id));
            let rData = records.filter(r => {
              if (isEmployee) return true;
              if (selectedEmployeeIds.length === 0) return true;
              const id1 = normalizeEmpId(r.empCode);
              const id2 = normalizeEmpId(r.employeeId);
              const id3 = normalizeEmpId(r.uid);
              return normSelected.includes(id1) || normSelected.includes(id2) || normSelected.includes(id3);
            });

            if (activeFilters.startDate) {
              rData = rData.filter(r => {
                const d = getNormalizedDate(r);
                return d ? d >= activeFilters.startDate : false;
              });
            }
            if (activeFilters.endDate) {
              rData = rData.filter(r => {
                const d = getNormalizedDate(r);
                return d ? d <= activeFilters.endDate : false;
              });
            }

            return [
              { label: 'Present', count: rData.filter(r => isRecordPresent(r)).length, color: '#28a745' },
              { label: 'Absent', count: rData.filter(r => getEffectiveStatus(r) === 'Absent').length, color: '#dc3545' },
              { label: 'Festival', count: rData.filter(r => getEffectiveStatus(r) === 'Festival').length, color: '#8e44ad' },
              { label: 'Half Days', count: rData.filter(r => getEffectiveStatus(r) === 'Half Days').length, color: '#17a2b8' },
              { label: 'Late', count: rData.filter(r => getEffectiveStatus(r) === 'Late').length, color: '#ffc107' },
              { label: 'Miss Punch', count: rData.filter(r => getEffectiveStatus(r) === 'Miss Punch').length, color: '#e83e8c' },
              { label: 'Early Exit', count: rData.filter(r => getEffectiveStatus(r) === 'Early Exit').length, color: '#fd7e14' },
              { label: 'Weekly Off', count: rData.filter(r => getEffectiveStatus(r) === 'Weekly Off').length, color: '#6c757d' },
            ].map((stat, idx) => {
              const isActive = activeQuickFilter === stat.label;
              return (
                <Col key={idx} xs={6} md={3} lg={true}>
                  <div
                    className={`bg-white p-2 p-sm-3 rounded-4 shadow-sm border-0 border-bottom border-4 h-100 cursor-pointer attendance-stat-card ${isActive ? 'shadow ring-2' : ''}`}
                    style={{
                      borderBottomColor: stat.color + ' !important',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      transform: isActive ? 'scale(1.05)' : 'scale(1)',
                      zIndex: isActive ? 5 : 1,
                      position: 'relative',
                      boxShadow: isActive ? `0 4px 20px ${stat.color}40` : ''
                    }}
                    onClick={() => setActiveQuickFilter(isActive ? null : stat.label)}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-bold" style={{ fontSize: '0.8rem', color: stat.color }}>{stat.label}</span>
                      <Badge
                        className="border-0 px-2 py-1 shadow-sm"
                        style={{
                          backgroundColor: stat.color,
                          color: 'white',
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                          fontWeight: '800'
                        }}
                      >
                        {rData.length > 0 ? ((stat.count / rData.length) * 100).toFixed(1) : 0}%
                      </Badge>
                    </div>
                    <h3 className="mb-1 fw-bold" style={{ color: isActive ? stat.color : '#333' }}>{stat.count}</h3>
                    <div className={`text-muted small fw-bold text-uppercase ${isActive ? 'text-primary' : ''}`} style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                      {stat.label} {isActive && <span className="ms-1">●</span>}
                    </div>
                  </div>
                </Col>
              );
            });
          })()}
        </Row>
      </div>

      {activeQuickFilter && (
        <Alert variant="info" className="mb-3 p-2 d-flex justify-content-between align-items-center rounded-3 bg-primary-subtle border-0">
          <small className="ms-2 fw-bold text-primary">Showing only: {activeQuickFilter}</small>
          <Button variant="link" size="sm" className="p-0 text-decoration-none fw-bold" onClick={() => setActiveQuickFilter(null)}>Reset Filter</Button>
        </Alert>
      )}

      {/* Ranking Insights Section */}
      {showRanking && renderRankingTable(filteredRecords)}

      {/* Statistics defined first to calculate counts on the full current month set */}
      {!loading && filteredRecords.length === 0 && (
        <Alert variant="light" className="text-center border-0 shadow-sm py-5 rounded-4 bg-white">
          <FaCalendarAlt className="mb-3 text-primary d-block mx-auto" style={{ fontSize: '2rem' }} />
          <h5 className="text-dark fw-bold">No Records Found</h5>
          <p className="text-muted mb-0">
            {activeFilters.startDate || activeFilters.endDate ? (
              <>No attendance data matches your current date range ({activeFilters.startDate ? formatDate(activeFilters.startDate) : 'Start'} to {activeFilters.endDate ? formatDate(activeFilters.endDate) : 'End'}).</>
            ) : (
              <>No attendance data matches your current filter selection for {getMonthName(month)} {year}.</>
            )}
          </p>
          {isHROrAdmin && <small className="d-block mt-2">Try adjusting filters or selecting a different month/year.</small>}
        </Alert>
      )}

      {filteredRecords.length > 0 && (
        <>
          {/* Mobile View Switcher (Cards vs Table) */}
          <div className="d-flex d-md-none justify-content-between align-items-center mb-3 px-1">
            <span className="small text-muted fw-bold">Records ({filteredRecords.length})</span>
            <div className="btn-group btn-group-sm shadow-sm" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <Button
                variant={mobileViewMode === 'cards' ? 'primary' : 'outline-secondary'}
                size="sm"
                className="fw-bold px-3 py-1"
                onClick={() => setMobileViewMode('cards')}
              >
                Cards
              </Button>
              <Button
                variant={mobileViewMode === 'table' ? 'primary' : 'outline-secondary'}
                size="sm"
                className="fw-bold px-3 py-1"
                onClick={() => setMobileViewMode('table')}
              >
                Table
              </Button>
            </div>
          </div>

          {/* Mobile Cards View (Visible on screens < 768px when Cards mode active) */}
          {mobileViewMode === 'cards' && (
            <div className="d-block d-md-none mb-3">
              {loading ? (
                <div className="text-center py-5 text-muted bg-white rounded-4 shadow-sm">Loading attendance data...</div>
              ) : (
                filteredRecords.map((record, idx) => (
                  <Card key={record.id || idx} className={`mb-3 border-0 shadow-sm rounded-4 overflow-hidden ${record.updatedAt ? 'border-start border-4 border-info' : ''}`}>
                    <Card.Body className="p-3">
                      {/* Top Row: Employee Name, Code, Date, and Status Badge */}
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="pe-2">
                          <h6 className="fw-bold mb-0 text-dark fs-6">{record.empName || record.employeeName || 'Unknown Staff'}</h6>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            <span className="fw-semibold text-secondary">{record.empCode || record.employeeId}</span> • {formatDate(record.date)}
                          </div>
                        </div>
                        <div className="text-end flex-shrink-0">
                          {getStatusBadge(record)}
                          {record.updatedAt && <div style={{ fontSize: '0.65rem', color: '#0d6efd', fontWeight: 600, marginTop: '2px' }}>Modified</div>}
                        </div>
                      </div>

                      {/* Middle Row: In / Out / Work Duration Matrix */}
                      <div className="bg-light p-2 rounded-3 mb-2">
                        <div className="row g-1 text-center">
                          <div className="col-4 border-end">
                            <span className="text-muted d-block" style={{ fontSize: '0.68rem', fontWeight: 700 }}>IN TIME</span>
                            <strong className="font-monospace text-dark" style={{ fontSize: '0.85rem' }}>{record.inTime || '-'}</strong>
                          </div>
                          <div className="col-4 border-end">
                            <span className="text-muted d-block" style={{ fontSize: '0.68rem', fontWeight: 700 }}>OUT TIME</span>
                            <strong className="font-monospace text-dark" style={{ fontSize: '0.85rem' }}>{record.outTime || '-'}</strong>
                          </div>
                          <div className="col-4">
                            <span className="text-muted d-block" style={{ fontSize: '0.68rem', fontWeight: 700 }}>WORK DUR.</span>
                            <strong className="text-dark" style={{ fontSize: '0.85rem' }}>{record.workDuration || record.workingHours || '-'}</strong>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Row: Shift, OT, Late, Early, and Edit Action */}
                      <div className="d-flex justify-content-between align-items-center pt-1">
                        <div className="d-flex gap-2 flex-wrap align-items-center text-muted" style={{ fontSize: '0.75rem' }}>
                          <span>Shift: <strong>{record.shift || '-'}</strong></span>
                          {(record.overtime || record.overtimeHours) && (
                            <span className="text-success fw-bold">OT: {record.overtime || `${record.overtimeHours} hrs`}</span>
                          )}
                          {record.lateBy && record.lateBy !== '-' && (
                            <span className="text-danger fw-bold">Late: {record.lateBy}</span>
                          )}
                          {record.earlyGoingBy && record.earlyGoingBy !== '-' && (
                            <span className="text-warning fw-bold">Early: {record.earlyGoingBy}</span>
                          )}
                        </div>
                        {isHROrAdmin && !isLocked && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="rounded-pill px-2 py-0 ms-auto"
                            style={{ fontSize: '0.75rem', height: '28px' }}
                            onClick={() => handleEdit(record)}
                          >
                            <FaEdit className="me-1" /> Edit
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Desktop Table View (Also active on mobile when 'Table' switcher is selected) */}
          <div className={`${mobileViewMode === 'table' ? 'd-block' : 'd-none d-md-block'} table-responsive attendance-table-container shadow-sm rounded-4 overflow-hidden border mb-3`}>
            <Table hover className="mb-0 bg-white">
              <thead className="bg-light border-bottom">
                <tr>
                  <th className="border-0 p-3 text-muted fw-bold small">DATE</th>
                  <th className="border-0 p-3 text-muted fw-bold small">EMPLOYEE</th>
                  <th className="border-0 p-3 text-muted fw-bold small text-center">IN</th>
                  <th className="border-0 p-3 text-muted fw-bold small text-center">OUT</th>
                  <th className="border-0 p-3 text-muted fw-bold small">SHIFT</th>
                  <th className="border-0 p-3 text-muted fw-bold small">STATUS</th>
                  <th className="border-0 p-3 text-muted fw-bold small text-center">WORK DUR.</th>
                  <th className="border-0 p-3 text-muted fw-bold small text-center">OT</th>
                  <th className="border-0 p-3 text-muted fw-bold small text-center">LATE</th>
                  <th className="border-0 p-3 text-muted fw-bold small text-center">EARLY</th>
                  {isHROrAdmin && !isLocked && <th className="border-0 p-3 text-muted fw-bold small text-center">ACTIONS</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={isHROrAdmin && !isLocked ? "11" : "10"} className="text-center py-5 text-muted">Loading attendance data...</td></tr>
                ) : (
                  filteredRecords.map((record, idx) => (
                    <tr key={record.id || idx} className={record.updatedAt ? 'table-info' : ''} title={record.updatedAt ? 'This record has been manually updated' : ''}>
                      <td>{formatDate(record.date)}</td>
                      <td>
                        <div>{record.empName || record.employeeName}</div>
                        <small className="text-muted">{record.empCode || record.employeeId}</small>
                      </td>
                      <td className="p-3 text-center align-middle font-monospace small">{record.inTime || '-'}</td>
                      <td className="p-3 text-center align-middle font-monospace small">{record.outTime || '-'}</td>
                      <td className="p-3 align-middle text-muted small">{record.shift || '-'}</td>
                      <td className="p-3 align-middle">
                        {getStatusBadge(record)}
                        {record.updatedAt && <div style={{ fontSize: '0.65rem', color: '#888', marginTop: '2px' }}>Modified</div>}
                      </td>
                      <td className="p-3 text-center align-middle small">{record.workDuration || record.workingHours || '-'}</td>
                      <td className="p-3 text-center align-middle small text-success font-bold">
                        {(() => {
                          if (record.overtime) return record.overtime;
                          if (record.overtimeHours) return `${record.overtimeHours} hrs`;

                          // Fallback dynamic calculation
                          if (record.outTime && record.outTime !== '-') {
                            const out = moment(record.outTime, ["HH:mm", "h:mm A", "H:mm"]);
                            if (out.isValid()) {
                              const outMins = out.hours() * 60 + out.minutes();
                              const threshold = 18 * 60 + 30; // 6:30 PM
                              if (outMins > threshold) {
                                const otMins = outMins - threshold;
                                return `${Math.floor(otMins / 60)}h ${otMins % 60}m`;
                              }
                            }
                          }
                          return '-';
                        })()}
                      </td>
                      <td className="p-3 text-center align-middle small text-danger font-bold">{record.lateBy || '-'}</td>
                      <td className="p-3 text-center align-middle small text-warning">{record.earlyGoingBy || '-'}</td>
                      {isHROrAdmin && !isLocked && (
                        <td className="p-3 text-center align-middle">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-primary p-0"
                            onClick={() => handleEdit(record)}
                          >
                            <FaEdit />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <div className="mt-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2">
        <div className="text-muted small">
          Showing {filteredRecords.length} records
        </div>
        <Pagination className="mb-0">
          <Pagination.Prev
            disabled={pagination.page === 1}
            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
          />
          <Pagination.Item active className="rounded-pill mx-1">{pagination.page}</Pagination.Item>
          <Pagination.Next
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
          />
        </Pagination>
      </div>

      <EditAttendanceModal
        show={showEditModal}
        record={selectedRecord}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
      />

      {/* Confirmation Modal to Delete Month's Attendance Sheet */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger fw-bold">
            <FaTrashAlt className="me-2" />
            Delete Monthly Attendance Sheet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete all attendance records for <strong>{getMonthName(month)} {year}</strong>?
          </p>
          <Alert variant="danger" className="mb-0 small">
            <strong>Warning:</strong> All {records.length} records in this month's attendance sheet will be permanently deleted from the database.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" className="fw-bold" onClick={handleDeleteMonthSheet} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Yes, Delete Sheet'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AttendanceTable;
